import { TwxPlanId, isTwxPlan, planRank } from './plan.catalog';

/**
 * The plan someone came for from twinnyx.com: its pricing's "Empezar con Growth" sends to
 * the app's root with `?plan=growth` ("Empezar gratis": `?plan=free` or nothing), maybe
 * with `?url=` (the website, from the hero's form) and `?lang=`. Read once when the app
 * boots (`captureTwxPlanIntent`, before the router drops the query on its redirects) and
 * kept in sessionStorage until it is used:
 *
 * - a new account: the sign-up's Plan step starts on it (a paid one is paid there, Stripe
 *   embedded, before going on);
 * - an account that already went through the sign-up: on entering the app, a higher plan
 *   goes straight to paying it (Ajustes → Plan, `?checkout=1`); a lower one (a downgrade)
 *   lands on Ajustes → Plan to do it there; the same plan, nothing.
 */
const KEY = 'twx-plan-intent';

export type TwxEntry = { plan: TwxPlanId | null; url: string | null; lang: string | null };

const read = (): TwxPlanId | null => {
    try {
        const value = sessionStorage.getItem(KEY);
        return isTwxPlan(value) ? value : null;
    } catch {
        return null;
    }
};

/** Reads the landing's query (`?plan=`); the rest of the entry (`url`, `lang`) is returned for the guard. */
export const captureTwxPlanIntent = (search = window.location.search): TwxEntry => {
    const params = new URLSearchParams(search);
    const plan = params.get('plan')?.toLowerCase() ?? null;
    if (isTwxPlan(plan)) {
        try {
            sessionStorage.setItem(KEY, plan);
        } catch {
            /* private mode: the intent lives as long as the query */
        }
    }
    return { plan: isTwxPlan(plan) ? plan : read(), url: params.get('url'), lang: params.get('lang') };
};

export const twxPlanIntent = (): TwxPlanId | null => read();

export const clearTwxPlanIntent = () => {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* nothing kept */
    }
};

/** What an onboarded account does with the intent: pay a higher plan, see a lower one, or nothing. */
export const intentAction = (wanted: TwxPlanId | null, current: TwxPlanId): 'upgrade' | 'downgrade' | null => {
    // "Empezar gratis" with a paid account is just coming in, never a request to downgrade.
    if (!wanted || wanted === 'free' || wanted === current) return null;
    return planRank(wanted) > planRank(current) ? 'upgrade' : 'downgrade';
};
